import DynamicModuleService, {
  type GridFetchOptions,
} from "@/services/APIs/DynamicModuleService";
import {
  getDecidedSchoolProfileStatus,
  getSchoolRecordId,
} from "@/project/karuna/utils/schoolProfileApproval";

/** Tuned for 3k–5k+ school rows: fewer round-trips, parallel page fetches. */
export const LARGE_GRID_FETCH: GridFetchOptions = {
  chunkSize: 500,
  maxPages: 100,
  pageConcurrency: 5,
};

const SCHOOL_PROFILE_SCHEMA = "School Profile";

/** Columns needed for admin school cards/list (grid_data often omits these). */
const SCHOOL_PROFILE_DISPLAY_COLUMNS = [
  "basicGrid",
  "basic_grid",
  "basicInfo",
  "basic_info",
  "kcno",
  "schoolname",
  "principal",
  "contact",
  "schoolemail",
  "kendra",
  "nearestkendra",
  "contactfax",
  "mobileno",
  "emailid",
  "status",
];

function mergeRecordFields(
  target: Record<string, unknown>,
  source: Record<string, unknown>
) {
  for (const [key, value] of Object.entries(source)) {
    if (value === undefined || value === null || value === "") {
      continue;
    }

    const existing = target[key];
    if (
      typeof value === "object" &&
      !Array.isArray(value) &&
      typeof existing === "object" &&
      existing !== null &&
      !Array.isArray(existing)
    ) {
      mergeRecordFields(
        existing as Record<string, unknown>,
        value as Record<string, unknown>
      );
      continue;
    }

    target[key] = value;
  }
}

/** Promote basicGrid/basicInfo values to the root so card mappers see flat fields. */
export function flattenSchoolProfileRecord(
  record: Record<string, unknown>
): Record<string, unknown> {
  const flattened: Record<string, unknown> = { ...record };

  const nestedSources = [
    record.basicGrid,
    record.basic_grid,
    record.basicInfo,
    record.basic_info,
  ].filter(
    (source): source is Record<string, unknown> =>
      source !== null &&
      source !== undefined &&
      typeof source === "object" &&
      !Array.isArray(source)
  );

  for (const nested of nestedSources) {
    mergeRecordFields(flattened, nested);
  }

  let parsedData: unknown = record.data;
  if (typeof parsedData === "string") {
    try {
      parsedData = JSON.parse(parsedData) as Record<string, unknown>;
    } catch {
      parsedData = undefined;
    }
  }

  if (
    parsedData &&
    typeof parsedData === "object" &&
    !Array.isArray(parsedData)
  ) {
    mergeRecordFields(flattened, parsedData as Record<string, unknown>);
    for (const key of [
      "basicGrid",
      "basic_grid",
      "basicInfo",
      "basic_info",
    ] as const) {
      const nested = (parsedData as Record<string, unknown>)[key];
      if (nested && typeof nested === "object" && !Array.isArray(nested)) {
        mergeRecordFields(flattened, nested as Record<string, unknown>);
      }
    }
  }

  return flattened;
}

/** Merge multiple School Profile payloads (list row + getItemById, grid + config, etc.). */
export function mergeSchoolProfileRecordSources(
  ...sources: Record<string, unknown>[]
): Record<string, unknown> {
  const merged: Record<string, unknown> = {};
  for (const source of sources) {
    if (source && typeof source === "object") {
      mergeRecordFields(merged, source);
    }
  }
  return flattenSchoolProfileRecord(merged);
}

function mergeSchoolProfileRows(gridRows: any[], configRows: any[]): any[] {
  const byId = new Map<string, Record<string, unknown>>();

  const upsert = (row: any) => {
    const id = getSchoolRecordId(row);
    if (!id) {
      return;
    }

    const existing = byId.get(id) ?? {};
    const decided =
      getDecidedSchoolProfileStatus(row) ||
      getDecidedSchoolProfileStatus(existing);
    const merged: Record<string, unknown> = { ...existing };
    mergeRecordFields(merged, row as Record<string, unknown>);
    const flattened = flattenSchoolProfileRecord(merged);
    // A stale "pending" in one source must not undo an approve/reject in another
    if (decided) {
      flattened.status = decided;
    }
    byId.set(id, flattened);
  };

  // Config/list API has flat fields; grid_data is often sparse — config first.
  for (const row of configRows) {
    upsert(row);
  }
  for (const row of gridRows) {
    upsert(row);
  }

  return Array.from(byId.values());
}

/**
 * Load all rows for a dynamic schema using grid_data paging, with a fallback
 * to the config list API when grid_data is empty or unavailable.
 */
export async function fetchSchemaGridRecordsAllPages(
  schemaName: string,
  options?: GridFetchOptions
): Promise<any[]> {
  try {
    const records = await DynamicModuleService.getGridColItemsAllPages(
      schemaName,
      options
    );
    if (records.length > 0) {
      return records;
    }
  } catch (gridError) {
    if (import.meta.env.DEV) {
      console.warn(
        `getGridColItemsAllPages failed for "${schemaName}", trying getGridCoByConfig:`,
        gridError
      );
    }
  }

  return DynamicModuleService.getGridCoByConfigAllPages(
    schemaName,
    [],
    {},
    options
  );
}

/**
 * School Profile list: grid_data for fast paging + config columns merge so cards
 * show principal, kendra, contact, email (grid rows alone are often sparse).
 */
export async function fetchSchoolProfileGridRecords(): Promise<any[]> {
  let configRows: any[] = [];

  // Full list records (empty columns) — same as AdminSchoolApproval; includes contact, etc.
  try {
    configRows = await DynamicModuleService.getGridCoByConfigAllPages(
      SCHOOL_PROFILE_SCHEMA,
      [],
      {},
      LARGE_GRID_FETCH
    );
  } catch (configError) {
    if (import.meta.env.DEV) {
      console.warn("School list API fetch failed:", configError);
    }
  }

  if (configRows.length === 0) {
    try {
      configRows = await DynamicModuleService.getGridCoByConfigAllPages(
        SCHOOL_PROFILE_SCHEMA,
        SCHOOL_PROFILE_DISPLAY_COLUMNS,
        {},
        LARGE_GRID_FETCH
      );
    } catch (columnConfigError) {
      if (import.meta.env.DEV) {
        console.warn(
          "School column-scoped list fetch failed:",
          columnConfigError
        );
      }
    }
  }

  let gridRows: any[] = [];
  try {
    gridRows = await DynamicModuleService.getGridColItemsAllPages(
      SCHOOL_PROFILE_SCHEMA,
      LARGE_GRID_FETCH
    );
  } catch (gridError) {
    if (import.meta.env.DEV) {
      console.warn("School grid_data fetch failed:", gridError);
    }
  }

  if (configRows.length > 0 || gridRows.length > 0) {
    return mergeSchoolProfileRows(gridRows, configRows);
  }

  return fetchSchemaGridRecordsAllPages(
    SCHOOL_PROFILE_SCHEMA,
    LARGE_GRID_FETCH
  );
}
